import { useEffect, useRef, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { MAX_PINS_PER_ATTACHMENT, MAX_PIN_TEXT_LENGTH } from '@feedback/shared';
import { strings } from '../strings';

/** One marker on the capture. Coordinates are 0..1 of the rendered image (ADR 0030). */
export interface CapturePin {
  x: number;
  y: number;
  text: string;
}

export interface CaptureStageProps {
  /** The capture being annotated. */
  blob: Blob;
  pins: CapturePin[];
  onPinsChange: (pins: CapturePin[]) => void;
  /** Re-captures the page and replaces `blob` in place. */
  onRetake: () => void;
  /** Drops the capture entirely, returning the modal to its capture-less state. */
  onRemove: () => void;
  /** True while a retake is in flight, so the control can say so rather than look dead. */
  retaking?: boolean;
  disabled?: boolean;
}

/**
 * The capture, leading the modal, with the markers a reporter dropped on it.
 *
 * **It leads because it is the thing a reporter has already got.** The capture
 * is taken on the click that opens the modal, so by the time anyone reads a
 * word of this form the most useful artifact in it already exists. Rendering it
 * as a 32px chip under the description, which is where it used to sit, asked
 * the reporter to write out what a picture in front of them already says
 * (I19222).
 *
 * A pin is placed by clicking the image AND by a button, and the button is not
 * a nicety: a pointer-only affordance is unusable by keyboard, and the widget
 * is embedded in products with their own accessibility commitments. The button
 * drops a pin in the middle and focuses its note, which is the same end state
 * as a click.
 */
export function CaptureStage({
  blob,
  pins,
  onPinsChange,
  onRetake,
  onRemove,
  retaking,
  disabled,
}: CaptureStageProps) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const imageRef = useRef<HTMLDivElement>(null);
  // Set when a pin has just been added, so its note can take focus once the
  // input exists. A ref rather than state: it is consumed by an effect and
  // never rendered, so storing it in state would cost a render for nothing.
  const focusPin = useRef<number | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(blob);
    setImageUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [blob]);

  useEffect(() => {
    if (focusPin.current === null) return;
    const index = focusPin.current;
    focusPin.current = null;
    const field = imageRef.current?.parentElement?.querySelector<HTMLInputElement>(
      `[data-ft-pin-note="${index}"]`,
    );
    field?.focus();
  }, [pins.length]);

  const atCapacity = pins.length >= MAX_PINS_PER_ATTACHMENT;

  const addPin = (x: number, y: number) => {
    if (atCapacity || disabled) return;
    focusPin.current = pins.length;
    onPinsChange([...pins, { x, y, text: '' }]);
  };

  const handleImageClick = (event: JSX.TargetedMouseEvent<HTMLDivElement>) => {
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return;
    // Clamped, because a pointer release can land a fraction outside the box
    // and a coordinate outside 0..1 is refused by the ingest schema.
    const x = Math.min(Math.max((event.clientX - rect.left) / rect.width, 0), 1);
    const y = Math.min(Math.max((event.clientY - rect.top) / rect.height, 0), 1);
    addPin(x, y);
  };

  const updatePin = (index: number, text: string) => {
    onPinsChange(pins.map((pin, i) => (i === index ? { ...pin, text } : pin)));
  };

  const removePin = (index: number) => {
    onPinsChange(pins.filter((_, i) => i !== index));
  };

  return (
    <div class="ft-capture">
      <div class="ft-capture-head">
        <span class="ft-capture-label">{strings.capture.heading}</span>
        <div class="ft-capture-actions">
          <button
            type="button"
            class="ft-capture-action"
            onClick={() => addPin(0.5, 0.5)}
            disabled={disabled || atCapacity}
          >
            {strings.capture.addPinLabel}
          </button>
          <button type="button" class="ft-capture-action" onClick={onRetake} disabled={disabled || retaking}>
            {retaking ? strings.capture.retakingLabel : strings.capture.retakeLabel}
          </button>
          <button type="button" class="ft-capture-action" onClick={onRemove} disabled={disabled}>
            {strings.capture.removeLabel}
          </button>
        </div>
      </div>

      <div
        ref={imageRef}
        class="ft-capture-image-wrap"
        role="group"
        aria-label={strings.capture.canvasLabel}
        onClick={handleImageClick}
      >
        {imageUrl ? <img src={imageUrl} alt={strings.capture.heading} class="ft-capture-image" /> : null}

        {pins.map((pin, index) => (
          <span
            key={`${pin.x}-${pin.y}-${index}`}
            class="ft-capture-pin"
            aria-hidden="true"
            style={{ left: `${pin.x * 100}%`, top: `${pin.y * 100}%` }}
          >
            {index + 1}
          </span>
        ))}
      </div>

      <p class="ft-capture-hint">
        {atCapacity ? strings.capture.pinLimitHint : strings.capture.instructions}
      </p>

      {pins.length > 0 ? (
        <ul class="ft-capture-pin-list">
          {pins.map((pin, index) => (
            <li key={`note-${index}`} class="ft-capture-pin-row">
              <span class="ft-capture-pin-number" aria-hidden="true">
                {index + 1}
              </span>
              <input
                class="ft-input"
                type="text"
                data-ft-pin-note={index}
                value={pin.text}
                maxLength={MAX_PIN_TEXT_LENGTH}
                placeholder={strings.capture.pinPlaceholder}
                aria-label={strings.capture.pinNoteLabel(index + 1)}
                onInput={(event) => updatePin(index, (event.target as HTMLInputElement).value)}
                disabled={disabled}
              />
              <button
                type="button"
                class="ft-capture-pin-remove"
                aria-label={strings.capture.removePinLabel(index + 1)}
                onClick={() => removePin(index)}
                disabled={disabled}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
